import { useEffect, useRef } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import { FiGlobe, FiHeadphones, FiMonitor } from 'react-icons/fi';
import { MarketingLayout } from '@/components/marketing/MarketingLayout';
import { Seo } from '@/components/marketing/Seo';
import { StoreBadges } from '@/components/marketing/StoreBadges';
import { FramedPhone, PhoneDuo, PhoneTrio } from '@/components/marketing/FramedPhone';
import { HeroDevices, WebLaptop } from '@/components/marketing/ProductMocks';
import { track } from '@/lib/analytics';
import { ANDROID_PLAY_STORE_URL, IOS_APP_STORE_URL } from '@/lib/store';
import { LIBRARY_STATS } from '@/content/library-stats';
import iconSrc from '@/assets/marketing/icon.png';
import flashcardColors from '@/assets/marketing/screens/flashcard-colors.png';
import flashcardFamily from '@/assets/marketing/screens/flashcard-family-txiv.png';
import flashcardAnimals from '@/assets/marketing/screens/flashcard-animals.png';
import yaya from '@/assets/marketing/screens/ya-ya.png';
import storybookMai from '@/assets/marketing/screens/storybook-mai.jpg';
import usefulWords from '@/assets/marketing/screens/useful-words.png';
import alphabet from '@/assets/marketing/screens/alphabet.png';
import bodyParts from '@/assets/marketing/screens/body-parts.png';
import bodyPartsMoob from '@/assets/marketing/screens/body-parts-moob.png';

export const Route = createFileRoute('/')({
  component: LandingPage,
});

const nunito = { fontFamily: "'Nunito', system-ui, sans-serif" } as const;
const khula = { fontFamily: "'Khula', system-ui, sans-serif" } as const;

const FEATURES = [
  {
    id: '01-hero',
    title: 'Learn Hmong',
    subtitle: 'Flashcards and games',
    highlight: 'Hmong',
    visual: (
      <PhoneTrio
        label="Flashcards for colors, family, and animals"
        screens={[
          { src: flashcardColors, alt: '' },
          { src: flashcardFamily, alt: '' },
          { src: flashcardAnimals, alt: '' },
        ]}
      />
    ),
  },
  {
    id: '02-yaya',
    title: 'Games',
    subtitle: 'Matching, spelling, and Ya! Ya!',
    highlight: 'Games',
    visual: (
      <div className="mx-auto w-[184px] md:w-[200px]">
        <FramedPhone src={yaya} alt="Ya! Ya! game. Tap to start" />
      </div>
    ),
  },
  {
    id: '03-storybook',
    title: 'Stories',
    subtitle: 'Illustrated Hmong storybooks',
    highlight: 'Stories',
    visual: (
      <div className="mx-auto w-[184px] md:w-[200px]">
        <FramedPhone src={storybookMai} alt="Mai storybook page" />
      </div>
    ),
  },
  {
    id: '04-terms',
    title: 'Term Sheets',
    subtitle: 'Grammar and everyday phrases',
    highlight: 'Term Sheets',
    visual: (
      <div className="mx-auto w-[184px] md:w-[200px]">
        <FramedPhone src={usefulWords} alt="Useful Words term sheet" />
      </div>
    ),
  },
  {
    id: '05-alphabet',
    title: 'The Alphabet',
    subtitle: 'Consonants, vowels, and tones',
    highlight: 'Alphabet',
    visual: (
      <div className="mx-auto w-[184px] md:w-[200px]">
        <FramedPhone src={alphabet} alt="Hmong alphabet consonants" />
      </div>
    ),
  },
  {
    id: '06-dialects',
    title: 'Two dialects',
    subtitle: 'White Hmong and Hmong Leng',
    highlight: 'dialects',
    visual: (
      <PhoneDuo
        label="Body parts in White Hmong and Hmong Leng"
        screens={[
          { src: bodyParts, alt: '' },
          { src: bodyPartsMoob, alt: '' },
        ]}
      />
    ),
  },
];

function withHighlight(text: string, highlight: string) {
  const index = text.indexOf(highlight);
  if (index < 0) return text;
  return (
    <>
      {text.slice(0, index)}
      <span className="text-primary">{highlight}</span>
      {text.slice(index + highlight.length)}
    </>
  );
}

export function LandingPage() {
  return (
    <MarketingLayout screen="Landing Screen">
      <Seo
        title="Hmonglus. Learn Hmong with flashcards and games"
        description="Learn White Hmong and Hmong Leng with flashcards, stories, pronunciation, and games. Available on the web, iPhone, and Android."
        path="/"
      />
      <HeroSection />
      <FeaturesSection />
      <StatsSection />
      <LibrarySection />
      <GetStartedSection />
    </MarketingLayout>
  );
}

function HeroSection() {
  return (
    <section className="relative overflow-x-hidden bg-primary pt-24 md:max-h-[1200px] md:overflow-hidden">
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 top-[45%] z-0 hidden md:block">
        <svg viewBox="0 0 1000 800" className="h-full w-full" preserveAspectRatio="none" aria-hidden>
          <defs>
            <linearGradient id="waveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(255, 255, 255, 0.2)" />
              <stop offset="100%" stopColor="rgba(255, 255, 255, 0.05)" />
            </linearGradient>
          </defs>
          <path
            d="M 0,120 Q 125,90 250,120 T 500,120 T 750,120 T 1000,120 L 1000,800 L 0,800 Z"
            fill="url(#waveGradient)"
            opacity="0.4"
          />
          <path
            d="M 0,120 Q 125,90 250,120 T 500,120 T 750,120 T 1000,120"
            fill="none"
            stroke="rgba(255, 255, 255, 0.25)"
            strokeWidth="3"
            opacity="0.5"
          />
          <path
            d="M 0,140 Q 150,110 300,140 T 600,140 T 900,140 T 1000,140 L 1000,800 L 0,800 Z"
            fill="rgba(255, 255, 255, 0.08)"
            opacity="0.25"
          />
        </svg>
      </div>
      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-6 px-6 pb-16 pt-6 lg:grid-cols-2 lg:gap-12 lg:pb-28 lg:pt-16">
        <div className="order-2 animate-fade-in lg:order-1">
          <h1
            className="mb-6 text-5xl font-light leading-tight tracking-tight text-white md:text-6xl lg:text-7xl"
            style={{ ...khula, letterSpacing: '-0.02em' }}
          >
            Learn Hmong
            <br />
            with <span className="text-cream">flashcards and games.</span>
          </h1>
          <p className="mb-8 max-w-md text-xl text-white/80" style={nunito}>
            Recorded words, illustrated stories, and lessons in White Hmong and Hmong Leng. Use it in a browser or in
            the iPhone and Android app.
          </p>
          <div className="mb-5">
            <StoreBadges source="hero" variant="cream" />
          </div>
          <Link
            to="/app/resources"
            onClick={() => track('Landing CTA Press', { cta: 'hero-learn' })}
            className="inline-flex text-sm font-semibold text-white/80 underline-offset-4 hover:text-white hover:underline"
            style={nunito}
          >
            Or start learning free in the browser
          </Link>
        </div>
        <div className="relative z-20 order-1 flex justify-center lg:order-2">
          <HeroDevices />
        </div>
      </div>
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-0">
        <svg viewBox="0 0 1440 120" className="h-auto w-full" preserveAspectRatio="none" aria-hidden>
          <path d="M0 120V60C240 20 480 0 720 0C960 0 1200 20 1440 60V120H0Z" fill="#F3FBF0" />
        </svg>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    let seen = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!seen && entry?.isIntersecting) {
          seen = true;
          track('Landing Features', { section: 'features' });
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="features" className="relative overflow-hidden bg-sky-light py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <div className="relative mb-16 text-center md:mb-20">
          <div className="mb-6 flex justify-center">
            <div className="flex aspect-square items-center justify-center rounded-full bg-primary p-4 md:p-5">
              <img src={iconSrc} alt="" className="h-14 w-14 rounded-2xl bg-white md:h-16 md:w-16" />
            </div>
          </div>
          <h2 className="mb-4 text-4xl font-bold text-gray-900 md:text-5xl" style={nunito}>
            What's in Hmonglus
          </h2>
          <p className="mx-auto max-w-2xl text-xl text-gray-600" style={nunito}>
            Flashcards, stories, term sheets, the alphabet, and games. The iPhone and Android app have the same
            lessons.
          </p>
        </div>
        <div className="space-y-16 lg:space-y-0">
          {[FEATURES.slice(0, 3), FEATURES.slice(3)].map((row, rowIndex) => (
            <div key={rowIndex === 0 ? 'features-row-learn' : 'features-row-tools'}>
              {rowIndex > 0 && (
                <div
                  aria-hidden
                  className="mx-auto mb-16 h-px max-w-3xl bg-gradient-to-r from-transparent via-[#C4EBC0] to-transparent lg:mb-24 lg:mt-6"
                />
              )}
              <div className="grid grid-cols-1 gap-y-16 lg:grid-cols-3 lg:gap-x-16 lg:gap-y-0">
                {row.map((feature, column) => {
                  const stagger =
                    column === 1 ? 'lg:translate-y-10' : column === 2 ? 'lg:translate-y-4' : '';
                  return (
                    <article
                      key={feature.id}
                      className={`flex flex-col items-center text-center ${stagger}`}
                    >
                      <div className="relative flex w-full items-end justify-center">
                        <div
                          aria-hidden
                          className="pointer-events-none absolute left-1/2 top-[54%] h-44 w-[90%] -translate-x-1/2 -translate-y-1/2 rounded-[50%] bg-[#ECFFE6] blur-2xl"
                        />
                        <div
                          aria-hidden
                          className="pointer-events-none absolute bottom-[8%] left-1/2 h-7 w-[58%] -translate-x-1/2 rounded-full bg-[#1f5610]/10 blur-xl"
                        />
                        <div className="relative z-10 flex min-h-[22rem] w-full items-end justify-center">
                          {feature.visual}
                        </div>
                      </div>
                      <h3
                        className="mt-8 text-[1.5rem] font-extrabold leading-none tracking-tight text-gray-900 md:text-[1.7rem]"
                        style={nunito}
                      >
                        {withHighlight(feature.title, feature.highlight)}
                      </h3>
                      <p
                        className="mt-2.5 max-w-[17rem] text-base font-semibold leading-snug text-gray-500"
                        style={nunito}
                      >
                        {withHighlight(feature.subtitle, feature.highlight)}
                      </p>
                    </article>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div
          aria-hidden
          className="mx-auto my-20 h-px max-w-3xl bg-gradient-to-r from-transparent via-[#C4EBC0] to-transparent md:my-24"
        />
        <div className="mx-auto flex max-w-4xl flex-col items-center text-center">
          <WebLaptop className="mb-8" />
          <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10">
            <FiMonitor className="h-7 w-7 text-primary" />
          </div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400" style={nunito}>
            Web app
          </p>
          <h3 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl" style={nunito}>
            Hmonglus on a computer
          </h3>
          <p className="max-w-2xl text-lg leading-relaxed text-gray-600" style={nunito}>
            The web app uses the same lessons, account, and Premium subscription as the mobile apps.
          </p>
          <Link
            to="/app/resources"
            onClick={() => track('Landing CTA Press', { cta: 'features-web' })}
            className="mt-6 rounded-lg bg-primary px-6 py-3 font-semibold text-white hover:bg-primary-hover"
            style={nunito}
          >
            Open the learning hub
          </Link>
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  const props = [
    { icon: FiHeadphones, title: 'Recorded audio', body: 'Adult and child voices for vocabulary and phrases.' },
    { icon: FiGlobe, title: 'Two dialects', body: 'White Hmong and Hmong Leng. You can switch dialects in the app.' },
    { icon: FiMonitor, title: 'Web and mobile', body: 'One Premium subscription covers the web app, iPhone, and Android.' },
  ];
  return (
    <section className="bg-primary py-20">
      <div className="mx-auto grid max-w-7xl gap-8 px-6 md:grid-cols-3 md:gap-12">
        {props.map((prop, index) => {
          const Icon = prop.icon;
          return (
            <div
              key={prop.title}
              className={`text-center ${index < props.length - 1 ? 'md:border-r md:border-white/20 md:pr-8' : ''}`}
            >
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/10">
                <Icon className="h-8 w-8 text-white" />
              </div>
              <h3 className="mb-3 text-xl font-bold text-white md:text-2xl" style={nunito}>
                {prop.title}
              </h3>
              <p className="leading-relaxed text-white/90" style={nunito}>
                {prop.body}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function LibrarySection() {
  return (
    <section className="bg-white py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-4xl font-bold text-gray-900 md:text-5xl" style={nunito}>
            Flashcards, stories, games, and grammar
          </h2>
          <p className="mx-auto max-w-2xl text-xl text-gray-600" style={nunito}>
            The library includes recorded audio and illustrations.
          </p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {LIBRARY_STATS.map((stat) => (
            <article key={stat.label} className="rounded-3xl bg-sky-light px-7 py-8">
              <p className="text-5xl font-extrabold tracking-tight text-primary md:text-6xl" style={nunito}>
                {stat.value}
              </p>
              <h3 className="mt-4 text-xl font-extrabold text-gray-900" style={nunito}>
                {stat.label}
              </h3>
              <p className="mt-1 text-base font-semibold text-gray-600" style={nunito}>
                {stat.detail}
              </p>
            </article>
          ))}
        </div>
        <p className="mx-auto mt-12 max-w-2xl text-center text-lg text-gray-600" style={nunito}>
          White Hmong and Hmong Leng versions are included.
        </p>
        <div className="mt-8 text-center">
          <Link
            to="/app/resources"
            onClick={() => track('Landing CTA Press', { cta: 'library-hub' })}
            className="inline-flex rounded-lg bg-primary px-6 py-3 font-semibold text-white hover:bg-primary-hover"
            style={nunito}
          >
            Browse the learning hub
          </Link>
        </div>
      </div>
    </section>
  );
}

function GetStartedSection() {
  const starts = [
    {
      href: IOS_APP_STORE_URL,
      external: true,
      icon: FaApple,
      title: 'App Store',
      detail: 'Download for iPhone',
      onClick: () => track('Landing Store Press', { store: 'ios', source: 'get-started' }),
    },
    {
      href: ANDROID_PLAY_STORE_URL,
      external: true,
      icon: FaGooglePlay,
      title: 'Google Play',
      detail: 'Download for Android',
      onClick: () => track('Landing Store Press', { store: 'android', source: 'get-started' }),
    },
    {
      href: '/app/resources',
      external: false,
      icon: FiMonitor,
      title: 'Browser',
      detail: 'Start learning free',
      onClick: () => track('Landing CTA Press', { cta: 'get-started-web' }),
    },
  ] as const;

  return (
    <section className="bg-sky-light py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-4xl font-bold text-gray-900 md:text-5xl" style={nunito}>
            Download the app or open the web version
          </h2>
          <p className="mx-auto max-w-2xl text-xl text-gray-600" style={nunito}>
            The same lessons are on iPhone, Android, and the web.
          </p>
        </div>
        <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
          {starts.map((start) => {
            const Icon = start.icon;
            const className =
              'flex flex-col items-center rounded-3xl bg-white px-6 py-10 text-center shadow-sm ring-1 ring-[#d9ead3] transition-transform hover:-translate-y-0.5 hover:shadow-md';
            const inner = (
              <>
                <span className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <Icon className="h-7 w-7" aria-hidden />
                </span>
                <h3 className="text-2xl font-extrabold text-gray-900" style={nunito}>
                  {start.title}
                </h3>
                <p className="mt-2 text-base font-semibold text-gray-600" style={nunito}>
                  {start.detail}
                </p>
              </>
            );
            return start.external ? (
              <a
                key={start.title}
                href={start.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={start.onClick}
                className={className}
              >
                {inner}
              </a>
            ) : (
              <Link key={start.title} to={start.href} onClick={start.onClick} className={className}>
                {inner}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
